/**
 * Copyright (c) 2018-2019 Aleksey Melnikov, True Directive Company.
 * @link https://truedirective.com/
 * @license MIT
*/
import { Component, Input, Output, HostBinding, EventEmitter, Renderer2, ElementRef,
         ChangeDetectorRef,
         ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { PopupComponent } from './popup.component';
import { DropdownBaseComponent } from './dropdown-base.component';

import { GridSettings, GridState } from '@true-directive/base';
import { Column, DataQuery } from '@true-directive/base';
import { GridComponent } from '../grid.component';
import { Keys, Utils } from '@true-directive/base';

@Component({
  selector: 'true-select',
  template:`
      <true-input-wrapper
        (btnClick)="btnClick($event)"
        [disabled]="disabled"
        class="true-select__input"
        [class.true-input_popup-visible]="popupVisible"
        [icon]="iconClass()">
        <input #input
               [(ngModel)]="displayValue"
               [readonly]="disableTextEditor"
               [attr.disabled]="disabled"
               [class.true-disable-te]="disableTextEditor"
               (input)="inputInput($event)"
               (click)="inputClick($event)"
               (touchstart)="inputTouchStart($event)"
               (touchmove)="inputTouchMove($event)"
               (touchend)="inputTouchEnd($event)"
               (blur)="inputBlur($event)"
               (focus)="inputFocus($event)"
               (keydown)="inputKeyDown($event)"/>
      </true-input-wrapper>
      <true-popup #popup
        (close)="popupClose($event)"
        (show)="popupShow($event)" >
        <true-grid #grid
                 [class.true-select__grid_hidden]="empty()"
                 class="true-select__grid"
                 [maxHeight]="maxDropDownHeight"
                 [columns]="columns"
                 [data]="items"
                 [settings]="settings"
                 (dataQuery)="gridDataQuery($event)"
                 (startProcess)="gridStartProcess($event)"
                 (endProcess)="gridEndProcess($event)"
                 (rowClick)="gridRowClick($event)">
        </true-grid>
        <div *ngIf="empty()" class="true-select__notfound" [style.width]="gridWidth()">ÐÐ¸ÑÐµÐ³Ð¾ Ð½Ðµ Ð½Ð°Ð¹Ð´ÐµÐ½Ð¾</div>
        <div *ngIf="updating()" class="true-select__updating">ÐÐ°Ð³ÑÑÐ·ÐºÐ°...</div>
      </true-popup>
    `,
    /*
      Removed: :host { display: inline-block; } - invalid ios rendering.
     */
  styles: [`

    .true-select__input {
      width: 100%;
    }

    input {
      box-sizing: border-box;
      width: 100%;
      height: 100%;
    }

    .true-select__grid_hidden {
      display: none;
    }

    .true-select__grid {
      border: none !important;
      cursor: pointer;
    }

    .true-select__notfound, .true-select__updating {
      padding: 0.6em;
      box-sizing: border-box;
      color: #aaa;
    }
  `],
  providers: [{
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true}]
  })
export class SelectComponent extends DropdownBaseComponent {

  /**
   * Current text value
   */
  public displayValue: string = '';

  /**
   * Current value
   */
  public set value(v: any) {
    if (v !== this._value) {
      this._value = v;
      this.writeValue(this._value);
      this.onChange(v);
    }
  }

  public get value() {
    return this._value;
  }

  // ÐÐ°Ð¿ÑÐ¾Ñ Ð´Ð°Ð½Ð½ÑÑ
  @Output('dataQuery')
  dataQuery: EventEmitter<any> = new EventEmitter<any>();

  private _focusedValue: any = null;
  private _valueField: string = null;

  @Input('valueField')
  public set valueField(fieldName: string) {
    this._valueField = fieldName;
    if (this._settings) {
      // ÐÐ°ÑÑÑÐ¾Ð¹ÐºÐ¸ Ð¼Ð¾Ð³ÑÑ Ð±ÑÑÑ ÐµÑÐµ Ð½Ðµ ÑÐ¾Ð·Ð´Ð°Ð½Ñ
      this._settings.keyField = this._valueField;
    }
  }

  public get valueField() {
    if (this._valueField === null && this.columns.length > 0) {
      return this.columns[0].fieldName;
    }
    return this._valueField === null ? 'id' : this._valueField;
  }

  private _displayField: string = null;
  @Input('displayField')
  public set displayField(fieldName: string) {
    this._displayField = fieldName;
  }

  public get displayField() {
    if (this._displayField === null && this.columns.length > 0) {
      return this.columns[0].fieldName;
    }
    return this._displayField === null ? 'name' : this._displayField;
  }

  @Input()
  columns: Array<Column> = [];

  @Input()
  items: Array<any>;

  @Input()
  parentState: GridState = null;

  private _settings: GridSettings = null;

  @Input('settings')
  set settings(v) {
    this._settings = v;
    this._settings.enableTouchScroll = false;
  }

  get settings() {
    if (!this._settings) {
      this._settings = GridSettings.minimal();
      this._settings.keyField = this.valueField;
      this._settings.widthUnit = 'em';
      this._settings.showHeader = false;
      this._settings.columnAutoWidth = false;
      this._settings.enableTouchScroll = false;
      this._settings.appearance.enableHoverAppearance = true;
    }
    return this._settings;
  }

  @Output('itemSelect')
  itemSelect: EventEmitter<any> = new EventEmitter();

  @ViewChild('grid', {static: true})
  grid: GridComponent;

  // ÐÑÐ±Ð¸ÑÐ°ÐµÐ¼ Ð»Ð¸ Ð¼Ñ Ð¸Ð· Ð³ÑÐ¸Ð´Ð°
  _onGrid: boolean = false;
  _empty = false;

  gridStartProcess(e: any) {
    if (this._empty && this.settings.requestData) {
      this._empty = false;
      this._updating = true;
    }
  }

  _updating = false;
  gridEndProcess(e: any) {
    setTimeout(() => {
      if (!this.popup.visible) {
        this.showByTarget();
      } else {
        // Let's update position of popup window. When list reduce, it may move
        // up or down regarding input.
        this.popup.updatePosition();
      }
    });

    this._updating = false;
    if (this.grid.resultRows.length === 0) {
      this._empty = true;
    } else {
      this._empty = false;
    }
  }

  gridDataQuery(e: any) {
    this.dataQuery.emit(e);
  }

  // ÐÑÐ¾Ð±ÑÐ°Ð¶Ð°ÐµÐ¼ Ð·Ð½Ð°ÑÐµÐ½Ð¸Ðµ Ð² ÐºÐ¾Ð¼Ð¿Ð¾Ð½ÐµÐ½ÑÐµ. Formatter: Ctrl --> View
  writeValue(v: any) {
    this._value = v;
    let found = this.items.find(item => item[this.valueField] === v);
    if (!found) {
      this.displayValue = '';
    } else {
      this.displayValue = found[this.displayField];
    }
  }

  // ÐÐ° Ð¾Ð´Ð¸Ð½ ÑÐ°Ð· ÑÑÑÐ°Ð½Ð°Ð²Ð»Ð¸Ð²Ð°ÐµÐ¼ Ð¸ Ð·Ð½Ð°ÑÐµÐ½Ð¸Ðµ Ð¸ Ð¾ÑÐ¾Ð±ÑÐ°Ð¶ÐµÐ½Ð¸Ðµ
  setValue(value: any, displayValue: string) {
    if (value !== this._value || displayValue !== this.displayValue) {
      this._focusedValue = value;
      this.displayValue = displayValue;
      this.value = value;
    }
  }

  // ÐÐ¾ Ð²Ð²ÐµÐ´ÐµÐ½Ð½Ð¾Ð¼Ñ ÑÐµÐºÑÑÑ Ð½Ð¸ÑÐµÐ³Ð¾ Ð½Ðµ Ð½Ð°Ð¹Ð´ÐµÐ½Ð¾?
  empty(): boolean {
    return this._empty;
  }

  // ÐÑÐ¾Ð¸ÑÑÐ¾Ð´Ð¸Ñ Ð¾Ð±Ð½Ð¾Ð²Ð»ÐµÐ½Ð¸Ðµ Ð´Ð°Ð½Ð½ÑÑ?
  updating(): boolean {
    return this._updating;
  }

  // ÐÑÐ²ÐµÑ
  fetchData(dataQuery: DataQuery, data: Array<any>) {
    let sel = this.getSelectedRow();
    if (sel) {
      this._focusedValue = sel[this.valueField];
    }

    if (this.grid && this.popupVisible) {
      this.grid.fetchData(dataQuery, data);

      if (this._focusedValue) {
        this.grid.locateByKey(this._focusedValue);
      } else {
        if (this._value !== null) {
          this.grid.locateByKey(this._value);
        }
      }
    }
  }

  /**
   * ÐÐ±ÑÐ°Ñ ÑÐ¸ÑÐ¸Ð½Ð° Ð³ÑÐ¸Ð´Ð° Ð² Ð²ÑÐ¿Ð°Ð´Ð°ÑÑÐµÐ¼ ÑÐ¿Ð¸ÑÐºÐµ
   * @return Ð¨Ð¸ÑÐ¸Ð½Ð° Ð² Ð·Ð°Ð´Ð°Ð½Ð½ÑÑ ÐµÐ´Ð¸Ð½Ð¸ÑÐ°Ñ Ð¸Ð·Ð¼ÐµÑÐµÐ½Ð¸Ñ
   */
  gridWidth() {
    let ww = 0;
    this.columns.forEach(c => ww += c.width);
    return ww + this.settings.widthUnit;
  }

  /**
   * ÐÐµÑÐµÐºÑÑÐ²Ð°ÐµÐ¼ Ð¿Ð¾ÐºÐ°Ð·ÑÐ²Ð°Ð½Ð¸Ðµ Ð²ÑÐ¿Ð°Ð´Ð°ÑÑÐµÐ³Ð¾ Ð¾ÐºÐ½Ð°, ÑÑÐ¾Ð±Ñ Ð¾Ð±Ð½Ð¾Ð²Ð¸ÑÑ Ð²ÑÑ Ð³ÑÐ¸Ð´Ð°
   * Ð¸ Ð²ÑÐ´ÐµÐ»Ð¸ÑÑ ÑÑÑÐ¾ÐºÑ, ÑÐ¾Ð´ÐµÑÐ¶Ð°ÑÑÑ ÑÐµÐºÑÑÐµÐµ Ð·Ð½Ð°ÑÐµÐ½Ð¸Ðµ
   * @param  e ÐÐ°ÑÐ°Ð¼ÐµÑÑÑ ÑÐ¾Ð±ÑÑÐ¸Ñ
   */
  popupShow(e: any) {
    this._onGrid = false;

    if (this._value !== null) {
        this.grid.locateByKey(this._value, this.valueField);
    } else {
      if (this.shownByKey) {
        this.grid.processKey(Keys.generateEvent(null, Keys.DOWN)); // ÐÑÐ´ÐµÑ Ð²ÑÐ±ÑÐ°Ð½Ð° Ð¿ÐµÑÐ²Ð°Ñ Ð·Ð°Ð¿Ð¸ÑÑ
      }
    }
    this.grid.checkSize();
  }

  /**
   * ÐÐ±ÑÐ°Ð±Ð¾ÑÑÐ¸Ðº ÐºÐ»Ð°Ð²Ð¸Ñ Ð²ÑÐ·ÑÐ²Ð°ÐµÑÑÑ Ð¸Ð· ÑÐ¾Ð´Ð¸ÑÐµÐ»ÑÑÐºÐ¾Ð³Ð¾ ÐºÐ»Ð°ÑÑÐ°
   * ÐÐ¾Ð·Ð²ÑÐ°ÑÐ°ÐµÑ false, ÐµÑÐ»Ð¸ ÐºÐ»Ð°Ð²Ð¸ÑÐ° Ð½Ðµ Ð¾Ð±ÑÐ°Ð±Ð¾ÑÐ°Ð½Ð°
   */
  processKey(e: any): boolean {

    if (e.keyCode === Keys.ENTER) {
      this.setValueFromGrid();
      this.popup.closePopup();
      return true;
    }

    if (!this.popupVisible) {
      return false;
    }

    if (e.keyCode === Keys.ESCAPE) {
      this.popup.closePopup();
      e.preventDefault();
      e.stopPropagation();
      return true;
    }

    if (e.keyCode === Keys.DOWN || e.keyCode === Keys.UP) {
      this._onGrid = true;
      this.grid.processKey(e);
      e.preventDefault();
      return true;
    }

    return false;
  }

  // ÐÐ»Ð¸Ðº Ð¿Ð¾ Ð·Ð°Ð¿Ð¸ÑÐ¸ Ð² Ð³ÑÐ¸Ð´Ðµ
  public gridRowClick(e: any) {
    this._onGrid = true;
    this._skipFocusOnPopupClose = false;
    this.value = e.row[this.valueField];
    this.popup.closePopup();
    this.itemSelect.emit(e.row);
  }

  // ÐÐ¾Ð²Ð¾ÑÐ¾Ñ Ð¸ÐºÐ¾Ð½ÐºÐ¸
  public iconClass() {
    let s = 'true-icon-angle-down true-turnable';
    if (this.popupVisible) {
      s += ' true-turned';
    }
    return s;
  }

  // ÐÐ²Ð¾Ð´ ÑÐµÐºÑÑÐ° Ð¿Ð¾Ð»ÑÐ·Ð¾Ð²Ð°ÑÐµÐ»ÐµÐ¼
  public inputInput(e: any = null) {
    // ÐÐºÐ¾ÑÐºÐ¾ ÐµÑÐµ Ð½Ð° Ð²ÑÐ¿Ð°Ð»Ð¾? Ð¿Ð¾ÐºÐ°Ð·ÑÐ²Ð°ÐµÐ¼
    if (!this.popupVisible && this.displayValue !== '') {
      this.grid.searchString = this.displayValue;
      return;
    }

    if (this.popupVisible) {
      setTimeout(() => {
        // Ð£ÑÑÐ°Ð½Ð°Ð²Ð»Ð¸Ð²Ð°ÐµÐ¼ ÑÐ¸Ð»ÑÑÑ Ð¿Ð¾ ÑÐµÐºÑÑÑ
        this.grid.searchString = this.displayValue;
      });
    }

    if (this.displayValue === '') {
      this.setValue(null, '');
      return;
    }

    this._value = null;
  }

  // ÐÐ¾Ð·Ð²ÑÐ°Ñ Ð²ÑÐ±ÑÐ°Ð½Ð½Ð¾Ð¹ ÑÑÑÐ¾ÐºÐ¸ Ð² Ð³ÑÐ¸Ð´Ðµ
  private getSelectedRow(): any {
    if (this.grid && this.grid.state.selection.ranges.length > 0) {
      let item = this.grid.state.selection.ranges[0].fromCell.row;
      return item;
    }
    return null;
  }

  private containsDisplayed(s: string): boolean {
    return !this.displayValue || (s !== undefined && s.toLowerCase().indexOf(this.displayValue.toLowerCase()) >= 0);
  }

  // Ð£ÑÑÐ°Ð½Ð¾Ð²ÐºÐ° Ð·Ð½Ð°ÑÐµÐ½Ð¸Ñ Ð¿Ð¾ Ð²ÑÐ´ÐµÐ»ÐµÐ½Ð½Ð¾Ð¹ Ð² Ð³ÑÐ¸Ð´Ðµ Ð·Ð°Ð¿Ð¸ÑÐ¸
  private setValueFromGrid() {

    if (!this.popupVisible && this.grid.state.model) {

      const found = this.grid.state.model.find(
          item => item[this.displayField].toLowerCase() === this.displayValue.toLowerCase()
      );

      if (found) {
        this.setValue(found[this.valueField], found[this.displayField]);
      } else {
        this.setValue(null, '');
      }
      return;
    }

    const sel = this.getSelectedRow();
    if (sel && (this._onGrid || this.containsDisplayed(sel[this.displayField]))) {
      this.setValue(sel[this.valueField], sel[this.displayField]);
    } else {
      this.setValueFromDisplayed();
    }
  }

  /**
   * Ð£ÑÑÐ°Ð½Ð¾Ð²ÐºÐ° Ð·Ð½Ð°ÑÐµÐ½Ð¸Ñ Ð¿Ð¾ Ð²Ð²ÐµÐ´ÐµÐ½Ð½Ð¾Ð¼Ñ ÑÐµÐºÑÑÑ
   */
  public setValueFromDisplayed() {
    // ÐÐ¾ Ð·Ð°Ð¿ÑÐ¾ÑÑ. Ð¡Ð¼Ð¾ÑÑÐ¸Ð¼ Ð² Ð³ÑÐ¸Ð´Ðµ. Ð¥Ð¾ÑÑ, ÐµÑÐ»Ð¸ Ð¿Ð¾Ð´ÑÐ¼Ð°ÑÑ, Ð¼Ð¾Ð¶Ð½Ð¾ Ð²ÑÐµÐ³Ð´Ð° Ð² Ð³ÑÐ¸Ð´Ðµ ÑÐ¼Ð¾ÑÑÐµÑÑ..
    let f = null;
    if (this.grid && this.grid.resultRows) {
      f = this.grid.resultRows.find(item => item[this.displayField].toLowerCase() === this.displayValue.toLowerCase());
    }
    if (f) {
      this.setValue(f[this.valueField], f[this.displayField]);
    } else {
      this.setValue(null, '');
    }
    return;
  }

  /**
   * ÐÐ¾ÑÐµÑÑ ÑÐ¾ÐºÑÑÐ° Ð¸Ð½Ð¿ÑÑÐ¾Ð¼
   * @param  e ÐÐ°ÑÐ°Ð¼ÐµÑÑÑ ÑÐ¾Ð±ÑÑÐ¸Ñ
   */
  inputBlur(e: FocusEvent) {
    this._skipFocusOnPopupClose = true;
    if ((this.value === null || this.value === undefined) && this.displayValue !== '') {

      if (e.relatedTarget && Utils.isAncestor(this.popup.elementRef.nativeElement, e.relatedTarget)) {
        // ÐÑÐ»Ð¸ ÑÐ¾ÐºÑÑ Ð¿ÐµÑÐµÑÐµÐ» Ð½Ð°...
        return;
      }

      this.closePopup();
      this.setValueFromDisplayed();
    }
    super.inputBlur(e);
  }

  constructor(
    protected _elementRef: ElementRef,
    protected _renderer: Renderer2) {
      super(_elementRef, _renderer);
      this.useAltDown = false;
  }
}
